import React, { useEffect, useState } from 'react';
import { addData, deleteFileByUrl, getData, handleFireBaseUpload } from '../Firebase/firestore';
import { db } from '../firebase';

import './resources.css';
import './embroidery.css';
import './main.css';
import './tuxedo.css';
import sh from '../assets/search.png';

/**
 * Embroidery Collections (admin)
 *
 * Firestore structure (read/write):
 * - Styles (unchanged): `Fabric/embroidery/styles/{styleId}`
 * - Single embroideries (selected in modal):
 *   `Fabric/embroidery/{targetCategory}/{targetType}/embroideries/{embId}`
 * - Collections (this page):
 *   `Fabric/embroidery/{targetCategory}/{targetType}/collections/{collectionId}`
 *
 * Storage structure (read/write):
 * - Collection icon: `Embroidery/Collections/{collectionId}/{fileName}`
 *   Note: when editing/replacing icon, we upload with a unique filename to avoid
 *   deleting the newly uploaded file (Firebase Storage URL token can change while the
 *   object path stays the same).
 *
 * View logic:
 * - Left panel shows Styles first (from `Fabric/embroidery/styles`).
 * - On style click, we list Collections that include that style (style id is inside
 *   collection `values[]`).
 * - Collections list can be filtered by Category + Cloth Type (part is NOT used as a
 *   filter for collections list, per requirement).
 * - Selection modal (for picking single embroideries into a collection) is filtered
 *   strictly by Category + Cloth Type + Part (no "All").
 */

const GARMENT_PARTS = {
  coat: ['base', 'lapel', 'collar', 'sleeve', 'back', 'flap'],
  shirt: ['base', 'collar', 'sleeve', 'back', 'pocket', 'flap'],
  kurta: ['base', 'collar', 'sleeve', 'back', 'pocket', 'flap'],
  sadri: ['base', 'collar'],
  vest: ['base', 'lapel'],
};

const normalizeKey = (v) => String(v || '').trim().toLowerCase();

const CATEGORIES = [
  { id: 'suits', label: 'Suit', icon: 'SUI', cloths: ['coat', 'vest', 'shirt'] },
  { id: 'formal', label: 'Formal', icon: 'FOR', cloths: ['shirt'] },
  { id: 'kurta', label: 'Kurta', icon: 'KUR', cloths: ['kurta', 'sadri', 'coat'] },
];

const SUB_CLOTH_TYPES = ['coat', 'vest', 'shirt', 'kurta', 'sadri'];

const GARMENT_LABELS = {
  coat: 'Coat',
  vest: 'Vest Coat',
  shirt: 'Shirt',
  kurta: 'Kurta',
  sadri: 'Sadri',
};

const formatGarmentLabel = (value) => GARMENT_LABELS[normalizeKey(value)] || String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getPartsForGarment = (garmentType) => GARMENT_PARTS[normalizeKey(garmentType)] || ['base'];

const formatPartLabel = (value) => String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const getEmbroideryBaseRef = ({ category, garmentType }) => {
  const cat = normalizeKey(category);
  const type = normalizeKey(garmentType);
  return db.collection('Fabric').doc('embroidery').collection(cat).doc(type);
};

const inferPlacementFromSelected = (renders, fallback = {}) => {
  const first = renders && renders.length ? renders[0] : null;
  const category = normalizeKey(first?.targetCategory || fallback.targetCategory || '');
  const garmentType = normalizeKey(first?.targetType || fallback.targetType || '');
  const part = normalizeKey(first?.targetPart || fallback.targetPart || '');
  return { category, garmentType, part };
};

function Embroidery_collection() {
  const [name, setName] = useState('');
  const [iconImage, setIconImage] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [existingIconUrl, setExistingIconUrl] = useState('');
  const [selectedEmb, setSelectedEmb] = useState([]);
  const [editingCollection, setEditingCollection] = useState(null);

  const [modal, setModal] = useState(false);
  const [modalCategoryFilter, setModalCategoryFilter] = useState('suits');
  const [modalGarmentFilter, setModalGarmentFilter] = useState('coat');
  const [modalPartFilter, setModalPartFilter] = useState(getPartsForGarment('coat')[0]);
  const [modalSearch, setModalSearch] = useState('');

  const currentCategory = CATEGORIES.find((c) => c.id === modalCategoryFilter) || CATEGORIES[0];
  const [embroideries, setEmbroideries] = useState([]);
  const [embLoaded, setEmbLoaded] = useState(false);
  const [stylesMap, setStylesMap] = useState({});

  const [pocketHidden, setPocketHidden] = useState(false);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [successModal, setSuccessModal] = useState(null);
  const [deletingUpload, setDeletingUpload] = useState(false);

  const [uploadedCollections, setUploadedCollections] = useState([]);
  const [collectionsCategoryFilter, setCollectionsCategoryFilter] = useState('suits');
  const [collectionsClothFilter, setCollectionsClothFilter] = useState('coat');
  const [listSearch, setListSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingCollection, setDeletingCollection] = useState(false);
  const [activeStyleId, setActiveStyleId] = useState(null);
  const currentCollectionCategory = CATEGORIES.find((c) => c.id === collectionsCategoryFilter) || CATEGORIES[0];

  useEffect(() => {
    const loadEmbroideries = async () => {
      try {
        const docRef = db.collection('Fabric').doc('embroidery');
        const stylesData = await getData(docRef.collection('styles'));

        const sMap = {};
        (stylesData || []).forEach((s) => { if (s && s.id) sMap[s.id] = s; });
        setStylesMap(sMap);
      } catch (err) {
        console.log('Failed to load embroideries:', err);
      } finally {
        setEmbLoaded(true);
      }
    };
    loadEmbroideries();
  }, []);

  useEffect(() => {
    const loadRendersForModal = async () => {
      try {
        const cat = normalizeKey(modalCategoryFilter);
        const garmentType = normalizeKey(modalGarmentFilter);
        if (!cat || !garmentType) {
          setEmbroideries([]);
          return;
        }
        const baseRef = getEmbroideryBaseRef({ category: cat, garmentType });
        const data = await getData(baseRef.collection('embroideries'));
        setEmbroideries(
          (data || []).map((d) => ({
            ...d,
            _category: cat,
            _garmentType: garmentType,
            _part: normalizeKey(d.targetPart),
            _typeKey: `${garmentType}_${normalizeKey(d.targetPart)}`,
            _refPath: `Fabric/embroidery/${cat}/${garmentType}/embroideries/${d.id}`,
          }))
        );
      } catch (err) {
        console.log('Failed to load modal renders:', err);
        setEmbroideries([]);
      }
    };
    loadRendersForModal();
  }, [modalCategoryFilter, modalGarmentFilter]);

  useEffect(() => {
    const allowedParts = getPartsForGarment(modalGarmentFilter);
    if (!allowedParts.includes(modalPartFilter) && allowedParts.length > 0) {
      setModalPartFilter(allowedParts[0]);
    }
  }, [modalGarmentFilter, modalPartFilter]);

  const loadUploadedCollections = async () => {
    try {
      // Load ALL collections across the hierarchy so that the left panel (Styles → Collections)
      // can work without depending on the "select embroideries" modal filters.
      const results = await Promise.all(
        CATEGORIES.flatMap((cat) =>
          (cat.cloths || SUB_CLOTH_TYPES).map(async (type) => {
            try {
              const baseRef = getEmbroideryBaseRef({ category: cat.id, garmentType: type });
              const data = await getData(baseRef.collection('collections'));
              return (data || []).map((d) => ({ ...d, _category: cat.id, _garmentType: type }));
            } catch (e) {
              return [];
            }
          })
        )
      );
      const merged = results.flat().sort((a, b) => {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return bi - ai;
      });
      setUploadedCollections(merged);
    } catch (err) {
      console.log('Failed to load uploaded collections:', err);
    }
  };

  useEffect(() => {
    loadUploadedCollections();
  }, []);

  useEffect(() => {
    const allowedCloths = currentCollectionCategory?.cloths || [];
    if (!allowedCloths.length || allowedCloths.includes(collectionsClothFilter)) return;
    setCollectionsClothFilter(allowedCloths[0]);
  }, [collectionsClothFilter, currentCollectionCategory]);

  const inferredPlacement = inferPlacementFromSelected(selectedEmb, editingCollection || {});
  const inferredCategory = inferredPlacement.category;
  const inferredGarmentType = inferredPlacement.garmentType;
  const inferredPart = inferredPlacement.part;

  const handleIconImageChange = (e) => {
    const file = e.target.files[0];
    setIconImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setIconPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setIconPreview(null);
    }
  };

  const filteredRenders = (embroideries || []).filter((r) => {
    if (normalizeKey(r.targetPart) !== normalizeKey(modalPartFilter)) return false;
    if (modalSearch && !JSON.stringify(r).toLowerCase().includes(modalSearch.toLowerCase())) return false;
    return true;
  });

  const isSelected = (r) => selectedEmb.some((s) => s.id === r.id && s.type === r._typeKey);

  const toggleSelect = (r) => {
    if (isSelected(r)) {
      setSelectedEmb((prev) => prev.filter((s) => !(s.id === r.id && s.type === r._typeKey)));
    } else {
      const cat = normalizeKey(r.targetCategory || r._category);
      const garmentType = normalizeKey(r.targetType || r._garmentType);
      const refPath = r._refPath || `Fabric/embroidery/${cat}/${garmentType}/embroideries/${r.id}`;
      setSelectedEmb((prev) => [
        ...prev,
        {
          id: r.id,
          type: r._typeKey || `${garmentType}_${normalizeKey(r.targetPart)}`,
          style: r.style || '',
          style_type: r.type || '',
          targetCategory: normalizeKey(r.targetCategory || r._category),
          targetType: normalizeKey(r.targetType || r._garmentType),
          targetPart: normalizeKey(r.targetPart),
          refPath,
        },
      ]);
    }
  };

  const findRenderBySel = (s) => (embroideries || []).find((r) => r.id === s.id);

  const getRenderThumb = (r) => {
    if (!r) return '';
    if (r.style && stylesMap[r.style] && stylesMap[r.style].src) {
      return stylesMap[r.style].src;
    }
    if (r.resources && typeof r.resources === 'object') {
      const vals = Object.values(r.resources).filter(Boolean);
      if (vals.length) return vals[0];
    }
    if (r.src) return r.src;
    return '';
  };

  const handleImgError = (e) => {
    e.currentTarget.style.display = 'none';
    const wrap = e.currentTarget.parentElement;
    if (wrap && !wrap.querySelector('.recom-button-card-image-fallback')) {
      const fallback = document.createElement('div');
      fallback.className = 'recom-button-card-image-fallback';
      fallback.textContent = 'No preview';
      wrap.appendChild(fallback);
    }
  };

  const resetForm = () => {
    setName('');
    setIconImage(null);
    setIconPreview(null);
    setExistingIconUrl('');
    setSelectedEmb([]);
    setEditingCollection(null);
    setPocketHidden(false);
  };

  const startEditCollection = (c) => {
    setEditingCollection(c);
    setName(c.name || '');
    setSelectedEmb(
      Array.isArray(c.values)
        ? c.values.map((item) => {
            if (item.targetCategory && item.targetType && item.targetPart && item.refPath) return item;
            const render = findRenderBySel(item);
            if (!render) return item;
            const cat = normalizeKey(render.targetCategory || render._category);
            const garmentType = normalizeKey(render.targetType || render._garmentType);
            return {
              ...item,
              targetCategory: normalizeKey(item.targetCategory || render.targetCategory || cat),
              targetType: normalizeKey(item.targetType || render.targetType || garmentType),
              targetPart: normalizeKey(item.targetPart || render.targetPart),
              refPath: item.refPath || `Fabric/embroidery/${cat}/${garmentType}/embroideries/${render.id}`,
            };
          })
        : []
    );
    setIconImage(null);
    setIconPreview(null);
    setExistingIconUrl(c.src || '');
    setPocketHidden(c.pocketHidden === true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) { alert('Name is required.'); return; }
    if (!editingCollection && !iconImage) { alert('Icon image is required.'); return; }
    if (!selectedEmb.length) { alert('Please select at least one embroidery render.'); return; }

    const placement = inferPlacementFromSelected(selectedEmb, editingCollection || {});
    const category = placement.category;
    const garmentType = placement.garmentType;
    const part = placement.part;
    if (!category || !garmentType) { alert('Unable to determine category/cloth type from selected renders.'); return; }

    const isEditing = !!editingCollection;
    const id = isEditing ? editingCollection.id : new Date().getTime().toString();
    const storageBase = `Embroidery/Collections/${id}`;
    const willUploadIcon = !!iconImage;

    setUploadTotal(willUploadIcon ? 1 : 0);
    setUploadProgress(0);
    setLoading(true);

    try {
      let icon_url = existingIconUrl;
      if (willUploadIcon) {
        setUploadStatus(isEditing ? 'Replacing icon image...' : 'Uploading icon image...');
        // Use a unique name on edit to avoid deleting the newly uploaded object when cleaning old URL.
        const fileName = isEditing ? `${id}_collection_icon_${Date.now()}` : `${id}_collection_icon`;
        icon_url = await handleFireBaseUpload(fileName, iconImage, storageBase);
        setUploadProgress((p) => p + 1);
        if (isEditing && editingCollection.src && editingCollection.src !== icon_url) {
          try { await deleteFileByUrl(editingCollection.src); } catch (err) { console.log('old icon cleanup skipped', err); }
        }
      }

      setUploadStatus(isEditing ? 'Updating collection...' : 'Saving to database...');

      const baseRef = getEmbroideryBaseRef({ category, garmentType });
      const coll_ref = baseRef.collection('collections');

      const valuesWithRefs = (selectedEmb || []).map((v) => {
        const vCat = normalizeKey(v.targetCategory || category);
        const vType = normalizeKey(v.targetType || garmentType);
        const vPart = normalizeKey(v.targetPart || part);
        const refPath = v.refPath || `Fabric/embroidery/${vCat}/${vType}/embroideries/${v.id}`;
        return { ...v, targetCategory: vCat, targetType: vType, targetPart: vPart, refPath };
      });

      await addData(coll_ref, id, {
        id,
        name,
        src: icon_url,
        values: valuesWithRefs,
        category,
        targetCategory: category,
        targetType: garmentType,
        targetPart: part,
        pocketHidden,
      });

      setSuccessModal({
        id,
        name,
        collectionLabel: `${category}/${garmentType}`,
        collectionType: `${category}/${garmentType}`,
        count: selectedEmb.length,
        iconUrl: icon_url,
        isEditing,
      });

      resetForm();
      loadUploadedCollections();
    } catch (err) {
      console.log('Embroidery collection save failed:', err);
      alert('Save failed. Please try again.');
    } finally {
      setLoading(false);
      setUploadStatus('');
    }
  };

  const deleteUploadedCollection = async () => {
    if (!successModal) return;
    try {
      setDeletingUpload(true);
      if (successModal.iconUrl) await deleteFileByUrl(successModal.iconUrl);
      const baseRef = getEmbroideryBaseRef({ category: inferredCategory, garmentType: inferredGarmentType });
      const coll_ref = baseRef.collection('collections');
      await coll_ref.doc(successModal.id).delete();
      setSuccessModal(null);
      loadUploadedCollections();
    } catch (err) {
      console.log('delete uploaded embroidery collection', err);
      alert('Unable to delete the uploaded embroidery collection.');
    } finally {
      setDeletingUpload(false);
    }
  };

  const partChips = getPartsForGarment(modalGarmentFilter).map((part) => ({ key: part, label: formatPartLabel(part) }));

  const collectionsByStyle = (() => {
    const map = {};
    uploadedCollections.forEach((c) => {
      const styleIds = new Set((c.values || []).map((v) => v && v.style).filter(Boolean));
      styleIds.forEach((sid) => {
        if (!map[sid]) map[sid] = [];
        map[sid].push(c);
      });
    });
    return map;
  })();

  const stylesWithCounts = Object.values(stylesMap)
    .map((s) => ({ ...s, _count: (collectionsByStyle[s.id] || []).length }))
    .sort((a, b) => b._count - a._count);

  const filteredStylesList = stylesWithCounts.filter((s) => {
    if (!listSearch) return true;
    return JSON.stringify(s).toLowerCase().includes(listSearch.toLowerCase());
  });

  const activeStyle = activeStyleId ? stylesMap[activeStyleId] : null;
  const collectionsForActiveStyle = activeStyleId ? (collectionsByStyle[activeStyleId] || []) : [];

  const filteredUploadedCollections = collectionsForActiveStyle.filter((c) => {
    if (collectionsCategoryFilter && normalizeKey(c.targetCategory || c._category) !== normalizeKey(collectionsCategoryFilter)) return false;
    if (collectionsClothFilter && normalizeKey(c.targetType || c._garmentType) !== normalizeKey(collectionsClothFilter)) return false;
    if (listSearch && !JSON.stringify(c).toLowerCase().includes(listSearch.toLowerCase())) return false;
    return true;
  });

  const confirmDeleteCollection = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingCollection(true);
      if (deleteTarget.src) {
        try { await deleteFileByUrl(deleteTarget.src); } catch (e) { console.log('icon delete skipped', e); }
      }
      const baseRef = getEmbroideryBaseRef({ category: deleteTarget.targetCategory || deleteTarget._category, garmentType: deleteTarget.targetType || deleteTarget._garmentType });
      const coll_ref = baseRef.collection('collections');
      await coll_ref.doc(deleteTarget.id).delete();
      setDeleteTarget(null);
      loadUploadedCollections();
    } catch (err) {
      console.log('delete embroidery collection', err);
      alert('Unable to delete the embroidery collection.');
    } finally {
      setDeletingCollection(false);
    }
  };

  return (
    <div className="button_upload_page button-upload-premium-page embroidery-render-page embroidery-collection-page">
      <div className="button_upload_form fabric-modern button-upload-premium-form">
        {editingCollection && (
          <div className="embroidery-collection-edit-banner">
            <div>
              <strong>Editing Collection</strong>
              <small>{editingCollection.name}</small>
            </div>
            <button type="button" className="embroidery-collection-edit-cancel" onClick={cancelEdit}>Cancel</button>
          </div>
        )}
        <form onSubmit={handleSubmit} className="button-upload-premium-fields">
          <div className="f-inputs">
            <label className="button-upload-label">Name <span className="text-red-500">*</span></label>
            <input type="text" placeholder="Enter collection name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="f-inputs">
            <div className="fabric-file-title"><b>Icon Image {!editingCollection && <span className="text-red-500">*</span>}</b></div>
            <input className="fabric-file-input" type="file" accept="image/*" onChange={handleIconImageChange} {...(!editingCollection ? { required: true } : {})} />
            {iconPreview ? (
              <div className="button-upload-preview">
                <img src={iconPreview} alt="Icon preview" />
              </div>
            ) : existingIconUrl ? (
              <div className="button-upload-preview">
                <img src={existingIconUrl} alt="Current icon" onError={handleImgError} />
                <div className="embroidery-collection-preview-hint">Current icon — pick a new file to replace</div>
              </div>
            ) : null}
          </div>

          <div className="f-inputs">
            <label className="button-upload-label">Select Embroideries <span className="text-red-500">*</span></label>
            <div className={`outfit-inline-fabric-select ${selectedEmb.length ? 'outfit-inline-fabric-select-active' : ''}`}>
              {selectedEmb.length ? (
                <div className="outfit-inline-fabric-value embroidery-collection-selected">
                  <div className="embroidery-collection-selected-thumbs">
                    {selectedEmb.slice(0, 6).map((s) => {
                      const r = findRenderBySel(s);
                      const thumb = getRenderThumb(r);
                      return thumb ? (
                        <img key={`${s.type}-${s.id}`} src={thumb} alt={(r && r.name) || 'Embroidery'} onError={handleImgError} />
                      ) : null;
                    })}
                    {selectedEmb.length > 6 && (
                      <span className="embroidery-collection-selected-more">+{selectedEmb.length - 6}</span>
                    )}
                  </div>
                  <div className="outfit-inline-fabric-copy">
                    <strong>{selectedEmb.length} embroidery render{selectedEmb.length === 1 ? '' : 's'} selected</strong>
                    <small>Click Change to edit the selection</small>
                  </div>
                  <div className="button-fabric-picker-actions">
                    <button type="button" className="outfit-inline-fabric-remove" onClick={() => setModal(true)}>Change</button>
                    <button type="button" className="outfit-inline-fabric-remove button-fabric-picker-clear" onClick={() => setSelectedEmb([])}>Clear</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="button-fabric-picker-trigger" onClick={() => setModal(true)}>
                  <span className="button-fabric-picker-trigger-label">Select embroidery renders</span>
                  <span className="button-fabric-picker-trigger-arrow">›</span>
                </button>
              )}
            </div>
            {selectedEmb.length ? (
              <div className="embroidery-collection-pocket-toggle">
                <span className="embroidery-collection-pocket-label">Pocket</span>
                <label className="emb-pocket-switch">
                  <input
                    type="checkbox"
                    checked={!pocketHidden}
                    onChange={(e) => setPocketHidden(!e.target.checked)}
                  />
                  <span className="emb-pocket-slider"></span>
                </label>
                <span className="emb-pocket-status">{pocketHidden ? 'Hidden' : 'Visible'}</span>
              </div>
            ) : null}
            {selectedEmb.length ? (
              <div className="embroidery-collection-auto-type">
                <span className="embroidery-collection-auto-type-label">Collection bucket</span>
                <span className="embroidery-collection-auto-type-value">
                  {inferredCategory && inferredGarmentType ? `${inferredCategory}/${inferredGarmentType} · ${inferredPart || '-'}` : 'Unknown — check selected renders'}
                </span>
                <small>Auto-detected from selected embroidery renders</small>
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="outfit-upload-progress">
              <div className="outfit-upload-progress-top">
                <span>{uploadStatus || 'Uploading...'}</span>
                <strong>{uploadTotal ? Math.floor((uploadProgress / uploadTotal) * 100) : 0}%</strong>
              </div>
              <div className="outfit-upload-progress-track">
                <div
                  className="outfit-upload-progress-fill"
                  style={{ width: `${uploadTotal ? (uploadProgress / uploadTotal) * 100 : 0}%` }}
                />
              </div>
              <div className="outfit-upload-progress-meta">
                {uploadProgress} / {uploadTotal} files uploaded
              </div>
            </div>
          ) : (
            <div className="embroidery-collection-actions">
              {editingCollection && (
                <button type="button" className="fabric-submit-btn fabric-submit-btn-secondary" onClick={cancelEdit}>
                  Cancel
                </button>
              )}
              <button type="submit" className="fabric-submit-btn">
                {editingCollection ? 'Update Collection' : 'Upload Embroidery Collection'}
              </button>
            </div>
          )}
        </form>
      </div>

      <div className="button-upload-list-shell embroidery-collection-list-shell">
        {activeStyle ? (
          <div className="embroidery-collection-breadcrumb">
            <button
              type="button"
              className="embroidery-collection-back-btn"
              onClick={() => {
                setActiveStyleId(null);
                setListSearch('');
                setCollectionsCategoryFilter('suits');
                setCollectionsClothFilter('coat');
              }}
            >
              ‹ Back to Styles
            </button>
            <h2 className="button-upload-list-title embroidery-collection-list-title">
              {activeStyle.name || 'Style'}
              <span className="embroidery-collection-list-count">{filteredUploadedCollections.length}</span>
            </h2>
          </div>
        ) : (
          <h2 className="button-upload-list-title">Uploaded Collections</h2>
        )}

        {!activeStyle ? (
          <div className="button-upload-filterbar">
            <div className="embroidery-view-toolbar">
              <div className="edit-fabric-search-box button-upload-search-box">
                <input
                  className="search_fabric_clothes"
                  placeholder="Search style"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
                <img src={sh} alt="Search" />
              </div>
            </div>
          </div>
        ) : (
          <div className="edit-fabric-filterbar edit-outfit-filterbar edit-outfit-filterbar-v2 embroidery-render-list-filterbar">
            <div className="edit-outfit-filter-row edit-outfit-filter-row-top">
              <div className="edit-outfit-category-tabs">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`edit-outfit-cat-tab ${collectionsCategoryFilter === cat.id ? 'active' : ''}`}
                    onClick={() => {
                      setCollectionsCategoryFilter(cat.id);
                      setCollectionsClothFilter(cat.cloths[0] || 'coat');
                    }}
                  >
                    <span className="edit-outfit-cat-tab-icon">{cat.icon}</span>
                    <span className="edit-outfit-cat-tab-label">{cat.label}</span>
                  </button>
                ))}
              </div>

              <div className="edit-outfit-filter-divider" />

              <div className="edit-outfit-cloth-chips">
                {(currentCollectionCategory?.cloths || []).map((cloth) => (
                  <button
                    key={cloth}
                    type="button"
                    className={`edit-outfit-chip ${collectionsClothFilter === cloth ? 'active' : ''}`}
                    onClick={() => setCollectionsClothFilter(cloth)}
                  >
                    {formatGarmentLabel(cloth)}
                  </button>
                ))}
              </div>
            </div>

            <div className="edit-outfit-filter-row">
              <div className="edit-outfit-search-box edit-outfit-search-box-v2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  className="search_fabric_clothes"
                  placeholder="Search collection"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {!activeStyle ? (
          <div className="button_uploads button-upload-list embroidery-collection-list embroidery-style-list">
            {filteredStylesList.length === 0 ? (
              <div className="add-brand-empty">No embroidery styles found.</div>
            ) : (
              filteredStylesList.map((s) => (
                <div
                  className={`button_upload_card button-upload-premium-card embroidery-collection-card embroidery-style-card ${s._count ? '' : 'embroidery-style-card-empty'}`}
                  key={`style-${s.id}`}
                  onClick={() => setActiveStyleId(s.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="button-upload-card-image-wrap">
                    {s.src ? (
                      <img src={s.src} alt={s.name || 'Style'} className="button-upload-card-image" onError={handleImgError} />
                    ) : (
                      <div className="recom-button-card-image-fallback">No preview</div>
                    )}
                    <span className="embroidery-style-card-badge">{s._count}</span>
                  </div>
                  <div className="button-upload-card-details">
                    <div className="button-upload-card-name" title={s.name || '-'}>{s.name || 'Untitled'}</div>
                    <div className="button-upload-card-specs">
                      <div className="button-upload-spec-row">
                        <span className="button-upload-spec-label">Type</span>
                        <span className="button-upload-spec-value">{s.type || '-'}</span>
                      </div>
                      <div className="button-upload-spec-row">
                        <span className="button-upload-spec-label">Collections</span>
                        <span className="button-upload-spec-value">{s._count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="button_uploads button-upload-list embroidery-collection-list">
            {filteredUploadedCollections.length === 0 ? (
              <div className="add-brand-empty">No collections use this style yet.</div>
            ) : (
              filteredUploadedCollections.map((c) => (
                <div className="button_upload_card button-upload-premium-card embroidery-collection-card" key={`${c.targetCategory || c._category}-${c.targetType || c._garmentType}-${c.id}`}>
                  <div className="button-upload-card-actions">
                    <button
                      type="button"
                      className="button-upload-edit-icon-btn"
                      onClick={() => startEditCollection(c)}
                      aria-label="Edit collection"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="button-upload-edit-icon-btn button-upload-delete-icon-btn"
                      onClick={() => setDeleteTarget(c)}
                      aria-label="Delete collection"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                        <path d="M10 11v6M14 11v6"></path>
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="button-upload-card-image-wrap">
                    {c.src ? (
                      <img src={c.src} alt={c.name || 'Collection'} className="button-upload-card-image" onError={handleImgError} />
                    ) : (
                      <div className="recom-button-card-image-fallback">No preview</div>
                    )}
                  </div>
                  <div className="button-upload-card-details">
                    <div className="button-upload-card-name" title={c.name || '-'}>{c.name || 'Untitled'}</div>
                    <div className="button-upload-card-specs">
                      <div className="button-upload-spec-row">
                        <span className="button-upload-spec-label">Type</span>
                        <span className="button-upload-spec-value">{`${c.targetCategory || c._category}/${c.targetType || c._garmentType}`}</span>
                      </div>
                      <div className="button-upload-spec-row">
                        <span className="button-upload-spec-label">Renders</span>
                        <span className="button-upload-spec-value">{Array.isArray(c.values) ? c.values.length : 0}</span>
                      </div>
                      <div className="button-upload-spec-row">
                        <span className="button-upload-spec-label">Pocket</span>
                        <span className="button-upload-spec-value">{c.pocketHidden ? 'Hidden' : 'Visible'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="outfit-success-modal-backdrop" onClick={() => !deletingCollection && setDeleteTarget(null)}>
          <div className="outfit-success-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="outfit-success-modal-badge outfit-success-modal-badge-danger">Confirm Delete</div>
            <h3>Delete this collection?</h3>
            <div className="outfit-success-modal-grid">
              <div><span>Name</span><strong>{deleteTarget.name || '-'}</strong></div>
              <div><span>Type</span><strong>{`${deleteTarget.targetCategory || deleteTarget._category || '-'} / ${deleteTarget.targetType || deleteTarget._garmentType || '-'}`}</strong></div>
              <div><span>Renders</span><strong>{Array.isArray(deleteTarget.values) ? deleteTarget.values.length : 0}</strong></div>
            </div>
            <button type="button" className="fabric-submit-btn outfit-success-close" onClick={() => setDeleteTarget(null)} disabled={deletingCollection}>
              Cancel
            </button>
            <button type="button" className="fabric-submit-btn outfit-delete-close" onClick={confirmDeleteCollection} disabled={deletingCollection}>
              {deletingCollection ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      )}

      {modal && (
        <div className="recom-modal-backdrop" onClick={() => setModal(false)}>
          <div className="recom-modal embroidery-style-picker-modal embroidery-collection-picker-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="recom-modal-header">
              <div>
                <h3>Select Embroidery Renders</h3>
                <p>Pick multiple renders to include in this collection. {selectedEmb.length} selected.</p>
              </div>
              <button type="button" className="recom-modal-close" onClick={() => setModal(false)} aria-label="Close">×</button>
            </div>
            <div className="embroidery-collection-filterbar">
              <div className="edit-outfit-filter-row edit-outfit-filter-row-top">
                <div className="edit-outfit-category-tabs">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      className={`edit-outfit-cat-tab ${modalCategoryFilter === cat.id ? 'active' : ''}`}
                      onClick={() => {
                        setModalCategoryFilter(cat.id);
                        setModalGarmentFilter((cat.cloths && cat.cloths.length ? cat.cloths[0] : SUB_CLOTH_TYPES[0]) || 'coat');
                        setModalPartFilter(getPartsForGarment((cat.cloths && cat.cloths.length ? cat.cloths[0] : SUB_CLOTH_TYPES[0]) || 'coat')[0]);
                      }}
                    >
                      <span className="edit-outfit-cat-tab-icon">{cat.icon}</span>
                      <span className="edit-outfit-cat-tab-label">{cat.label}</span>
                    </button>
                  ))}
                </div>

                <div className="edit-outfit-filter-divider" />

                <div className="edit-outfit-cloth-chips">
                  {(currentCategory?.cloths || SUB_CLOTH_TYPES).map((cl) => (
                    <button
                      key={cl}
                      type="button"
                      className={`edit-outfit-chip ${modalGarmentFilter === cl ? 'active' : ''}`}
                      onClick={() => setModalGarmentFilter(cl)}
                    >
                      {formatGarmentLabel(cl)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="edit-outfit-filter-row embroidery-collection-filter-row-bottom">
                <div className="recom-modal-chips embroidery-collection-part-chips">
                  {partChips.map((chip) => (
                    <button
                      type="button"
                      key={`p-${chip.key}`}
                      className={`recom-modal-chip ${modalPartFilter === chip.key ? 'active' : ''}`}
                      onClick={() => setModalPartFilter(chip.key)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                <div className="recom-modal-search embroidery-collection-search">
                  <input type="text" placeholder="Search render" value={modalSearch} onChange={(e) => setModalSearch(e.target.value)} />
                  <img src={sh} alt="Search" />
                </div>
              </div>
            </div>
            <div className="recom-modal-body">
              {!embLoaded ? (
                <div className="recom-modal-empty">Loading embroidery renders...</div>
              ) : filteredRenders.length === 0 ? (
                <div className="recom-modal-empty">No embroidery renders found.</div>
              ) : (
                <div className="recom-modal-grid">
                  {filteredRenders.map((r) => {
                    const selected = isSelected(r);
                    const thumb = getRenderThumb(r);
                    const styleDoc = r.style ? stylesMap[r.style] : null;
                    return (
                      <div
                        key={`${r._category}-${r._garmentType}-${r.id}`}
                        className={`recom-button-card ${selected ? 'selected' : ''}`}
                        onClick={() => toggleSelect(r)}
                      >
                        <div className="recom-button-card-image-wrap">
                          {thumb ? (
                            <img src={thumb} alt={(styleDoc && styleDoc.name) || r.name || 'Style'} className="recom-button-card-image" onError={handleImgError} />
                          ) : (
                            <div className="recom-button-card-image-fallback">No preview</div>
                          )}
                          {selected && (<span className="recom-button-card-check" aria-hidden>✓</span>)}
                        </div>
                        <div className="recom-button-card-details">
                          <div className="recom-button-card-name" title={r.name || '-'}>{r.name || 'Untitled'}</div>
                          <div className="recom-button-card-specs">
                            {styleDoc && (
                              <div className="recom-button-spec-row">
                                <span className="recom-button-spec-label">Style</span>
                                <span className="recom-button-spec-value">{styleDoc.name || '-'}</span>
                              </div>
                            )}
                            <div className="recom-button-spec-row">
                              <span className="recom-button-spec-label">Garment</span>
                              <span className="recom-button-spec-value">{r.targetType || r._garmentType || '-'}</span>
                            </div>
                            <div className="recom-button-spec-row">
                              <span className="recom-button-spec-label">Category</span>
                              <span className="recom-button-spec-value">{r.targetCategory || r._category || '-'}</span>
                            </div>
                            <div className="recom-button-spec-row">
                              <span className="recom-button-spec-label">Part</span>
                              <span className="recom-button-spec-value">{formatPartLabel(r.targetPart || r._part || '-')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="recom-modal-footer embroidery-collection-modal-footer">
              <div className="recom-modal-summary">{selectedEmb.length} selected</div>
              <div className="recom-modal-actions">
                <button type="button" className="outfit-inline-fabric-remove button-fabric-picker-clear" onClick={() => setSelectedEmb([])}>Clear all</button>
                <button type="button" className="fabric-submit-btn" onClick={() => setModal(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {successModal && (
        <div className="outfit-success-modal-backdrop">
          <div className="outfit-success-modal">
            <div className="outfit-success-modal-badge">{successModal.isEditing ? 'Update Successful' : 'Upload Successful'}</div>
            <h3>{successModal.isEditing ? 'Embroidery collection updated successfully' : 'Embroidery collection uploaded successfully'}</h3>
            <div className="outfit-success-modal-grid">
              <div><span>ID</span><strong>{successModal.id}</strong></div>
              <div><span>Name</span><strong>{successModal.name}</strong></div>
              <div><span>Collection</span><strong>{successModal.collectionLabel}</strong></div>
              <div><span>Embroideries</span><strong>{successModal.count}</strong></div>
            </div>
            <button type="button" className="fabric-submit-btn outfit-success-close" onClick={() => setSuccessModal(null)}>
              Close
            </button>
            {!successModal.isEditing && (
              <button
                type="button"
                className="fabric-submit-btn outfit-delete-close"
                onClick={() => {
                  if (deletingUpload) return;
                  if (window.confirm(`Are you sure you want to delete ${successModal?.name || 'this uploaded embroidery collection'}? This action cannot be undone.`)) {
                    deleteUploadedCollection();
                  }
                }}
                disabled={deletingUpload}
              >
                {deletingUpload ? 'Deleting...' : 'Delete This Upload'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Embroidery_collection;
